import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import ModalTemplate from '@/shared/modal/modal-template';
import useFilters from '@/app/hooks/useFilters';
import { useOpen } from '@/app/hooks';
import { Dialog } from '@mui/material';
import { useGetDocsQuery } from '@/entities/didox/api';
import { useTranslation } from 'react-i18next';
import { DidoxDocumentsModalRef, DidoxModalFormValues, ResolveCallbackValue } from './types';
import { DidoxDocument, GetDidoxDocsParams } from '@/entities/didox/types';
import {
  CreateDealDocumentType,
  RequestDocumentDidoxType,
  RequestDocumentType,
} from '@/features/create-seller-request-form/types';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { DidoxDocumentsModalTopContent } from './components/DidoxDocumentsModalTopContent';
import { DidoxDocumentsModalTable } from './components/DidoxDocumentsModalTable';
import { DidoxDocumentsModalActions } from './components/DidoxDocumentsModalActions';

interface Props {
  partnerTin: string;
  type: 'seller' | 'buyer';
  documents: {
    value: RequestDocumentType;
    type: CreateDealDocumentType;
    required?: boolean;
  }[];
}

export const DidoxDocumentsModal = forwardRef<DidoxDocumentsModalRef, Props>(
  ({ partnerTin, documents: docFields, type }, ref) => {
    const { t } = useTranslation();
    const { open, setOpen } = useOpen();
    const form = useForm<DidoxModalFormValues>({
      defaultValues: {
        dateType: 'created',
        search: '',
        dates: null,
      },
    });
    const [doctype, setDoctype] = useState<string | string[] | null>(null);
    const [isMultiSelect, setIsMultiSelect] = useState(true);
    const [selected, setSelected] = useState<DidoxDocument[]>([]);
    const [preSelectedDocIds, setPreSelectedDocIds] = useState<string[]>([]);
    const initialSelectedRef = useRef<DidoxDocument[]>([]);
    const defaultFilters: GetDidoxDocsParams = {
      page: 1,
      limit: 5,
      owner: type === 'seller' ? 1 : 0,
      status: 3,
      doctype: '',
      partner: partnerTin,
    };

    const { filters, updateFilters } = useFilters({
      defaultFilters,
    });

    const {
      data: documents,
      isFetching: isDocumentsFetching,
      isLoading: isDocumentsLoading,
    } = useGetDocsQuery(filters, {
      skip: !open,
    });

    const resolveCallback =
      useRef<(value: ResolveCallbackValue | PromiseLike<ResolveCallbackValue>) => void>();
    const rejectCallback = useRef(() => {});

    useImperativeHandle(ref, () => ({
      open: onOpen,
    }));

    const onOpen: DidoxDocumentsModalRef['open'] = (d, isMulti) => {
      return new Promise((res, rej) => {
        const isInvoice = Array.isArray(d)
          ? d.some((el) => el === '002' || el === '008')
          : ['002', '008'].includes(d);

        const params: GetDidoxDocsParams = {
          ...defaultFilters,
          doctype: d || '',
          partner: d !== '075' ? partnerTin : '',
        };

        const contract = docFields.find((doc) => doc.type === '007');

        if (isInvoice && contract?.value && 'contract_number' in contract.value) {
          params.contractDate = contract.value.contract_date;
          params.contractName = contract.value.contract_number;
        }

        if (isInvoice && isMulti) {
          const preSelected = docFields
            .filter(
              (doc) =>
                (doc.type === '002' || doc.type === '008') &&
                doc.value !== null &&
                typeof doc.value === 'object' &&
                'doc_id' in doc.value
            )
            .map((doc) => {
              const { unique: _unique, ...rest } = doc.value as RequestDocumentDidoxType;
              return rest as DidoxDocument;
            });
          setSelected(preSelected);
          setPreSelectedDocIds(preSelected.map((d) => d.doc_id));
          initialSelectedRef.current = preSelected;
        } else {
          setSelected([]);
          setPreSelectedDocIds([]);
          initialSelectedRef.current = [];
        }

        updateFilters(params, { reset: true });
        setOpen(true);
        setDoctype(d);
        setIsMultiSelect(!!isMulti);
        resolveCallback.current = res;
        rejectCallback.current = rej;
      });
    };

    const resetFilters = () => {
      const params: GetDidoxDocsParams = {
        ...defaultFilters,
        doctype: doctype || '',
        partner: doctype !== '075' ? partnerTin : '',
      };

      updateFilters(params, { reset: true });
    };

    const onClose = (isReject?: boolean) => {
      const needReject = typeof isReject === 'undefined' || isReject;
      setOpen(false);
      setSelected([]);
      setPreSelectedDocIds([]);
      initialSelectedRef.current = [];
      setTimeout(() => {
        form.reset();
      }, 300);
      if (needReject) {
        rejectCallback.current?.();
      }
    };

    const onChoose = (doc: DidoxDocument) => {
      resolveCallback.current?.([doc]);
      setOpen(false);
    };

    const isInvoiceAdded = docFields.some(
      (doc) => (doc.type === '002' || doc.type === '008') && doc.value && 'doc_id' in doc.value
    );
    const isContractAdded = docFields.some(
      (doc) => doc.type === '007' && doc.value && 'contract_number' in doc.value
    );

    const handleCreateVerificationAct = () => {
      const invoices = docFields.filter(
        (doc) => (doc.type === '002' || doc.type === '008') && doc.value && 'doc_id' in doc.value
      );
      const contract = docFields.find(
        (doc) => doc.type === '007' && doc.value && 'contract_number' in doc.value
      );
      if (!contract || !contract.value || !('contract_number' in contract.value)) return;
      const params = new URLSearchParams();
      params.append('docType', '052');
      params.append('dateDoc', dayjs().format('YYYY-MM-DD'));
      params.append('partner__inn', partnerTin);
      params.append('contracts.value.0.actName', contract?.value.contract_number);
      params.append('contracts.value.0.actDate', contract?.value.contract_date);

      if (invoices.length) {
        invoices.forEach((invoice, idx) => {
          if (invoice.value && 'doc_id' in invoice.value) {
            params.append(
              `contracts.value.0.verificationActs.value.${idx}.FacturaId`,
              invoice?.value.doc_id
            );
            params.append(
              `contracts.value.0.verificationActs.value.${idx}.OwnerOperationDate`,
              invoice?.value.doc_date
            );
            params.append(
              `contracts.value.0.verificationActs.value.${idx}.OwnerOperationName`,
              invoice?.value.name
            );
            params.append('verificationActsLength', invoices.length.toString());
          }
        });
      }

      const url =
        import.meta.env.VITE_DIDOX_BASE_URL + '/create_document' + '?' + params.toString();
      window.open(url, '_blank');
    };

    const handleCreateFounderProtocol = () => {
      const url = import.meta.env.VITE_DIDOX_BASE_URL + `/document_form/${doctype}`;

      window.open(url, '_blank');
    };

    const onSubmitFilters = (data: DidoxModalFormValues) => {
      const { search, dateType, dates } = data;
      const params: GetDidoxDocsParams = {};

      params.name = search;

      if (dates) {
        const [from, to] = dates;

        if (dateType === 'created') {
          params.docDateFromCreated = dayjs(from).format('YYYY-MM-DD');
          params.docDateToCreated = dayjs(to).format('YYYY-MM-DD');
        } else {
          params.docDateFromUpdated = dayjs(from).format('YYYY-MM-DD');
          params.docDateToUpdated = dayjs(to).format('YYYY-MM-DD');
        }
      }

      updateFilters(params, { skipEmpty: true });
    };

    const onSubmit = () => {
      const newlySelected = selected.filter(
        (doc) => !initialSelectedRef.current.some((init) => init.doc_id === doc.doc_id)
      );
      resolveCallback.current?.(newlySelected);
      setSelected([]);
      setPreSelectedDocIds([]);
      initialSelectedRef.current = [];
      setOpen(false);
    };

    return (
      <Dialog
        open={open}
        onClose={() => onClose(true)}
        PaperProps={{
          sx: {
            height: '80vh',
            maxWidth: 1200,
            width: '100%',
          },
        }}
      >
        <ModalTemplate
          title={
            t('select') +
            ' ' +
            t(`doctypes.${Array.isArray(filters.doctype) ? filters.doctype[0] : filters.doctype}`)
          }
          onClose={() => onClose()}
          noBtns
          dialogBottomContent={
            <DidoxDocumentsModalTopContent
              form={form}
              doctype={doctype}
              isContractAdded={isContractAdded}
              isInvoiceAdded={isInvoiceAdded}
              onCreateVerificationAct={handleCreateVerificationAct}
              onCreateFounderProtocol={handleCreateFounderProtocol}
              onSubmit={onSubmitFilters}
              resetFilters={resetFilters}
            />
          }
          actionsTopContent={
            <DidoxDocumentsModalActions
              documents={documents}
              filters={filters}
              updateFilters={updateFilters}
              selected={selected}
              onSubmit={onSubmit}
              setSelected={setSelected}
            />
          }
        >
          <DidoxDocumentsModalTable
            documents={documents}
            isLoading={isDocumentsLoading}
            isFetching={isDocumentsFetching}
            onChoose={onChoose}
            selected={selected}
            setSelected={setSelected}
            isMultiSelect={isMultiSelect}
            preSelectedDocIds={preSelectedDocIds}
          />
        </ModalTemplate>
      </Dialog>
    );
  }
);
